import * as React from 'react';
import axios from 'axios';
import type {
  InventoryUploadResponse,
  UploadInventoryReportPayload,
} from 'contracts/inventory-2/uploads';
import { useAuth } from 'hooks/auth';
import { inventory2Api } from 'services';

const STATUS_MESSAGE: Record<number, string> = {
  400: 'Arquivo ausente ou extensão inválida.',
  401: 'Acesso ao serviço de inventory não autorizado.',
  422: 'Planilha rejeitada. O banco não foi alterado.',
  500: 'O arquivo ficou no armazenamento, mas o banco não mudou.',
  502: 'O armazenamento recusou o arquivo. O banco não foi alterado.',
  503: 'Serviço sem armazenamento ou sem banco.',
};

const readApiError = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') return undefined;

  const payload = data as { error?: unknown };
  return typeof payload.error === 'string' ? payload.error : undefined;
};

export const toInventoryUploadError = (error: unknown): Error => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error : new Error('Não foi possível enviar a planilha.');
  }

  if (!error.response) {
    return new Error('Não foi possível conectar ao serviço de inventory.');
  }

  const apiMessage = readApiError(error.response.data);
  const fallback = STATUS_MESSAGE[error.response.status] || 'Não foi possível enviar a planilha.';

  return new Error(apiMessage || fallback);
};

export const useUploadInventoryReport = () => {
  const { profile } = useAuth();

  const uploadInventoryReport = React.useCallback(async (payload: UploadInventoryReportPayload) => {
    if (!inventory2Api.defaults.baseURL) {
      throw new Error('Serviço de inventory indisponível.');
    }

    const formData = new FormData();
    formData.append('file', payload.file, payload.file.name);

    if (profile?.userId != null && profile.name) {
      formData.append('userId', String(profile.userId));
      formData.append('userName', profile.name);
    }

    try {
      const { data } = await inventory2Api.post<InventoryUploadResponse>(
        `/${payload.uploadType}/upload`,
        formData,
      );
      return data;
    } catch (error) {
      throw toInventoryUploadError(error);
    }
  }, [profile?.userId, profile?.name]);

  return { uploadInventoryReport };
};
